import React, { useEffect, useState } from 'react'
import { Box, Chip, Pagination, Stack, Typography } from '@mui/material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { useAuth0 } from '@auth0/auth0-react'
import { logEvent } from '../../../analytics/analytics'
import { ensureAnonId } from '../../../helpers/anon-id'
import { ROUTES } from '../../../helpers/routes-helper'
import useAppUtilities from '../../../hooks/useAppUtilities'
import {
  fetchMyLiveSessions,
  fetchMySessions,
  GroupSession,
} from '../../../services/groupSessionService'
import { errorLog } from '../../../utils/log-helper'

const SESSIONS_PAGE_SIZE = 5

interface PlannerSessionsListProps {
  /** Which arm rendered the list — stamped on the open event so arms stay comparable. */
  /** Where the list is rendered, for the open event's screen_name. */
  screenName: string
  /** Told the parent whether the user has any planners, so it can adapt its copy. */
  onLoaded?: (count: number) => void
}

/**
 * "Your planners" — the live sessions this device or account already belongs to, so a user can
 * return without the share link. Extracted from the old create screen so BOTH experiment arms show
 * the identical list: the only thing the experiment varies is how a NEW planner gets created.
 *
 * The list merges two endpoints and dedupes by share token: the anon-keyed list (device) and the
 * eater-keyed list (JWT). After claim-on-signup nulls the device anon-id the anon list goes empty,
 * so the authenticated path is the only way back in-app.
 */
const PlannerSessionsList: React.FC<PlannerSessionsListProps> = ({
  screenName,
  onLoaded,
}) => {
  const { navigate } = useAppUtilities()
  const { isAuthenticated } = useAuth0()
  const [mySessions, setMySessions] = useState<GroupSession[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)

  useEffect(() => {
    let alive = true
    void (async () => {
      try {
        const anonId = await ensureAnonId()
        const [anonSessions, liveSessions] = await Promise.all([
          fetchMySessions(anonId),
          isAuthenticated ? fetchMyLiveSessions() : Promise.resolve([]),
        ])
        if (!alive) return
        const byToken = new Map<string, GroupSession>()
        for (const s of [...anonSessions, ...liveSessions]) {
          if (s.valid && s.shareToken) byToken.set(s.shareToken, s)
        }
        const merged = Array.from(byToken.values())
        setMySessions(merged)
        onLoaded?.(merged.length)
      } catch (e) {
        errorLog('[PlannerSessionsList] fetch failed', e)
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => {
      alive = false
    }
    // Deliberately keyed on auth only: onLoaded is a reporting callback, and re-running on its
    // identity would refetch the list on every parent render.
  }, [isAuthenticated])

  const pageCount = Math.ceil(mySessions.length / SESSIONS_PAGE_SIZE)
  const paged = mySessions.slice(
    (page - 1) * SESSIONS_PAGE_SIZE,
    page * SESSIONS_PAGE_SIZE,
  )

  // Keep the page in range when the list shrinks (e.g. after the fetch resolves or a dedup).
  useEffect(() => {
    if (page > 1 && page > pageCount) {
      setPage(Math.max(1, pageCount))
    }
  }, [page, pageCount])

  const open = (shareToken: string): void => {
    logEvent('session_opened', {
      share_token: shareToken,
      screen_name: screenName,
    })
    navigate(`${ROUTES.DINE}/${shareToken}`)
  }

  if (loading || mySessions.length === 0) {
    return null
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>
        Your planners
      </Typography>
      <Stack spacing={1.5}>
        {paged.map((s) => (
          <Box
            key={s.shareToken}
            onClick={() => open(s.shareToken as string)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              p: 2,
              borderRadius: 2,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              backgroundColor: 'background.paper',
              cursor: 'pointer',
              '&:hover': { backgroundColor: 'action.hover' },
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {s.name && s.name.trim() ? s.name : 'Dinner'}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {s.location ? `${s.location} · ` : ''}
                {s.memberCount} joined
              </Typography>
            </Box>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexShrink={0}
            >
              <Chip size="small" label="Open" variant="outlined" />
              <ChevronRightIcon color="action" />
            </Stack>
          </Box>
        ))}
      </Stack>
      {pageCount > 1 && (
        <Stack alignItems="center" sx={{ mt: 2 }}>
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, p) => setPage(p)}
            color="primary"
            size="small"
          />
        </Stack>
      )}
    </Box>
  )
}

export default PlannerSessionsList
